import BrowserId from './identification/browserId'
import SessionId from './identification/sessionId'
import TrackingId from './identification/trackingId'
import AttributionModel from './attribution'
import Traits from './traits'
import Department from './department'
import CompositeEventTracker from './trackers/CompositeEventTracker'
import DecoratingEventTracker from './trackers/DecoratingEventTracker'
import AuthorizingEventTracker from './trackers/AuthorizingEventTracker'
import { EventTracker } from './trackers/EventTracker'
import { Application } from './types/applications'
import { UserTraits } from './types/traits'
import { Identity } from './constants'
import { Tracker, TrackingContext } from './types/tracker'
import acquisitionAttributesFromWindow from './util/acquisitionAttributesFromWindow'
import store from './store'

import AsayerTracker from './trackers/destinations/AsayerTracker'
import ConsoleTracker from './trackers/destinations/ConsoleTracker'
import FacebookTracker from './trackers/destinations/FacebookTracker'
import RedditAdsTracker from './trackers/destinations/RedditAdsTracker'
import SegmentTracker from './trackers/destinations/SegmentTracker'

// This is the bag of "exports" exposed to the .configure() callback.
const ConfigurationContext = {
  AsayerTracker,
  ConsoleTracker,
  FacebookTracker,
  RedditAdsTracker,
  SegmentTracker,
  Identity,
}

export type TrackerFactory = (client: TrackingClient) => Tracker

export type TrackingClientOptions = {
  application: Application
  trackers: TrackerFactory[]
  sessionId: string
  identity?: Identity
}

export default class TrackingClient {
  private _application?: Application

  private _trackers: Tracker[]

  private _identity: Identity

  private _disabled: boolean

  private _browserId: BrowserId
  private _sessionId: SessionId
  private _trackingId: TrackingId

  private _traits: Traits

  private _attribution: AttributionModel

  private _department: Department

  private _pageKey?: string

  /**
   * The public event tracking API.
   *
   * See: DecoratingEventTracker
   */
  public events: DecoratingEventTracker

  public constructor() {
    this._disabled = false

    this._identity = store.get('identity-type') || Identity.Guest

    // Initialize identifiers
    this._sessionId = new SessionId()
    this._browserId = new BrowserId()
    this._trackingId = new TrackingId()

    // Initialize user traits
    this._traits = new Traits()

    // Initialize the user's department
    this._department = new Department()

    // Initialize attribution models
    this._attribution = new AttributionModel()

    // Initialize the trackers
    this._trackers = []

    this.events = new DecoratingEventTracker(
      this,
      new AuthorizingEventTracker(
        this,
        new CompositeEventTracker(() => {
          // See the note in `EventTracker.ts` for why this duck-typing is necessary.
          return this._trackers.filter((tracker) => {
            return tracker.hasOwnProperty('__EVENT_TRACKER__')
          }) as EventTracker[]
        })
      )
    )
  }

  public get application() {
    return this._application
  }

  public get browserId() {
    return this._browserId.toString()
  }

  public get sessionId() {
    return this._sessionId.toString()
  }

  public get trackingId() {
    return this._trackingId.toString()
  }

  public get firstTouch() {
    return this._attribution.firstTouch
  }

  public get lastTouch() {
    return this._attribution.lastTouch
  }

  public get identified() {
    return this._identity !== Identity.Guest
  }

  public get identity() {
    return this._identity
  }

  public get disabled() {
    return this._disabled
  }

  public configure(
    configurationFn: (context: typeof ConfigurationContext) => TrackingClientOptions
  ) {
    const options = configurationFn(ConfigurationContext)

    // Set session ID
    this._sessionId.set(options.sessionId)

    // Set application context
    this._application = options.application

    // Set identity type
    if (options.identity) {
      this._identity = options.identity || this._identity
      store.set('identity-type', this._identity)
    }

    // Create and register each tracker
    options.trackers.forEach((trackerFactory) => {
      const tracker = trackerFactory(this)

      this._trackers.push(tracker)
    })
  }

  /**
   * Generate a snapshot of the current state of the tracked user. This context is attached
   *   to each individual tracking event as well as calls to `identify` and `touch`.
   *
   * Trackers could derive this data directly from the TrackingClient, but this method allows
   *   us to ensure that the data associated with each event is accurate to the exact moment
   *   the event was instantiated (or when `identify`/`touch` was called), vs when the event
   *   was processed by the Tracker.
   *
   * To ensure that Tracker implementations aren't susceptible to race conditions, Trackers
   *   shouldn't access the `TrackingClient` directly (including this method), and should
   *   instead rely only on the context passed along with each method call.
   */
  public getTrackingContext(): TrackingContext {
    return {
      browserId: this.browserId,
      sessionId: this.sessionId,
      trackingId: this.trackingId,

      application: this.application!,

      identity: this.identity,

      attribution: {
        lastTouch: this.lastTouch ? Object.assign({}, this.lastTouch) : undefined,
        firstTouch: this.firstTouch ? Object.assign({}, this.firstTouch) : undefined,
      },

      traits: Object.assign({}, this.getTraits()),

      pageKey: this.getPageKey(),

      department: this.getDepartment(),
    }
  }

  /**
   * Mark the start of a new attribution session and associate it with acquisition data.
   *
   * This method attempts to achieve a level of idempotency. This method is a no-op if either
   *   of the following are true:
   *
   *  - The window was loaded by reloading/refreshing the page.
   *  - The window's referrer has the same host as the active URL (i.e. the user is not coming
   *      from an external host).
   *
   * See: https://dealdotcom.atlassian.net/wiki/spaces/ENGINEERING/pages/13467664/Sessionization
   */
  public touch() {
    if (this._disabled) {
      return
    }

    const attributes = acquisitionAttributesFromWindow()

    // N.B.: The `window.performance.navigation` API is deprecated in favor of the Navigation
    //   Timing Level 2 specification, but the newer specification has poor support currently.
    //   Notably, Safari is missing support for the newer API. For now, this works well.
    const isPageRefresh =
      window.performance &&
      window.performance.navigation.type === window.performance.navigation.TYPE_RELOAD

    // N.B.: `initAppUrl` === `window.location.href` and `referrerUrl` === `window.document.referrer`
    //   (assuming the attributes are populated using the utils exported by this library)
    const isReferredBySameHost =
      attributes.initAppUrl &&
      attributes.referrerUrl &&
      new URL(attributes.initAppUrl).host === new URL(attributes.referrerUrl).host

    // Ignore invalid touch points
    if (isPageRefresh || isReferredBySameHost) {
      return
    }

    this._attribution.touch(attributes)

    this._trackers.forEach((tracker) => {
      if (tracker.touch) {
        tracker.touch(attributes, this.getTrackingContext())
      }
    })
  }

  /**
   * Set the TrackingId and identity type for the tracked user.
   *
   * This method is idempotent: subsequent consecutive calls with the same ID are ignored. This is
   *   true even if the identity type is different. In other words, you can not update the identity
   *   type without also updating the TrackingId.
   */
  public identify(trackingId: string, identity: Identity) {
    if (this._disabled) {
      return
    }

    if (this._trackingId.toString() === trackingId) {
      return
    }

    this._identity = identity
    store.set('identity-type', this._identity)

    this._trackingId.set(trackingId)

    this._trackers.forEach((tracker) => {
      if (tracker.identify) {
        tracker.identify(trackingId, this.getTrackingContext())
      }
    })
  }

  /**
   * Add traits (PII) to the tracked user.
   *
   * Traits are overwritten in place.
   */
  public addTraits(traits: UserTraits) {
    if (this._disabled) {
      return
    }

    if (traits === undefined) {
      return this._traits
    }

    this._traits.add(traits)

    // Allow individual trackers to reset their internal state
    this._trackers.forEach((tracker) => {
      if (tracker.addTraits) {
        tracker.addTraits(traits)
      }
    })
  }

  public getTraits() {
    return this._traits.get()
  }

  /**
   * Set the department for the tracked user (by a department slug)
   *
   * Note: The definition of a user's department is loose. Consumers can shop across
   *   departments and be acquired from marketing associated with multiple departments.
   *   This field is only used to fire department-specific pixels for certain pixel
   *   implementations (e.g. Facebook) in order to build audiences for similar consumers.
   *   It should *not* be used for general department analytics, or attached to our own
   *   tracking events.
   */
  public setDepartment(departmentSlug: string) {
    if (this._disabled) {
      return
    }

    this._department.set(departmentSlug)

    this._trackers.forEach((tracker) => {
      if (tracker.setDepartment) {
        tracker.setDepartment(departmentSlug)
      }
    })
  }

  /**
   * Get the current department for the tracked user.
   *
   * Note: It is generally a bad idea to rely on this field externally. See the note in
   *   `setDepartment` for more context.
   */
  public getDepartment() {
    return this._department.get()
  }

  /**
   * Set the current  page key for future tracked events. This should be updated as the
   *   navigates around the site.
   *
   * Note: Unlike most other tracking context (e.g. the current department), the page key
   *   is stored in memory vs. localStorage. Events fired before setting the page key
   *   will *never* have a page key set, even if the user was previously tracked in the
   *   same browser.
   */
  public setPageKey(pageKey?: string) {
    if (this._disabled) {
      return
    }

    this._pageKey = pageKey

    this._trackers.forEach((tracker) => {
      if (tracker.setPageKey) {
        tracker.setPageKey(pageKey)
      }
    })
  }

  /**
   * Get the current page key.
   */
  public getPageKey() {
    return this._pageKey
  }

  /**
   * Enable session recording (via a session recording tracker like Asayer)
   *
   * Note: All logged-in users will be recorded automatically, and a sample of logged-out
   *   users will be recorded as well. Use this method to explicitly enable session
   *   recording for "high value" users.
   */
  public record() {
    if (this._disabled) {
      return
    }

    this._trackers.forEach((tracker) => {
      if (tracker.record) {
        tracker.record()
      }
    })
  }

  /**
   * Reset all state associated with the tracked user: identifiers, attribution data, and
   *   PII. The notable exception is the `deviceId` which is persisted forever.
   */
  public reset(newSessionId: string) {
    if (this._disabled) {
      return
    }

    // Reset the identified flag
    this._identity = Identity.Guest

    // Reset the session and tracking IDs
    this._sessionId.reset(newSessionId)
    this._trackingId.reset()

    // Reset the local attribution model
    this._attribution.reset()

    // Reset the user traits
    this._traits.reset()

    // Reset the user's department
    this._department.reset()

    // Reset the page key
    this._pageKey = undefined

    // Allow individual trackers to reset their internal state
    this._trackers.forEach((tracker) => {
      if (tracker.reset) {
        tracker.reset()
      }
    })
  }

  /**
   * Disable event tracking (e.g. when a user is being impersonated)
   */
  public disable() {
    if (this._disabled) {
      return
    }

    this._disabled = true

    // Allow individual trackers to respond to being disabled
    this._trackers.forEach((tracker) => {
      if (tracker.disable) {
        tracker.disable()
      }
    })
  }

  /**
   * Enable event tracking
   */
  public enable() {
    if (!this._disabled) {
      return
    }

    this._disabled = false

    this._trackers.forEach((tracker) => {
      if (tracker.enable) {
        tracker.enable()
      }
    })
  }
}
